import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import {
  heroSlides as mockHeroSlides,
  insights as mockInsights,
  capabilities as mockCapabilities,
  stats as mockStats,
  magazineFeatures as mockMagazineFeatures,
} from "../mock";
import ArticleCard from "../components/ArticleCard";
import InstagramFeed from "../components/InstagramFeed";
import { getHome, getInsights, getCapabilities, getMagazine } from "../api";
import { useI18n } from "../i18n/I18nContext";

const Hero = ({ slides }) => {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);
  const slide = slides[active];

  return (
    <section className="relative h-[86vh] min-h-[560px] w-full overflow-hidden bg-[#0a2540]">
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a2540] via-[#0a2540]/80 to-transparent" />
        </div>
      ))}

      <div className="relative h-full max-w-[1400px] mx-auto px-5 md:px-8 flex items-center">
        <div className="max-w-2xl text-white fade-up" key={slide.id}>
          <span className="inline-block text-sm font-700 uppercase tracking-widest text-[#5eb0f5] mb-4">
            {slide.eyebrow}
          </span>
          <h1 className="font-display font-800 text-4xl md:text-6xl leading-[1.05]">
            {slide.title}
          </h1>
          <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-xl">
            {slide.excerpt}
          </p>
          <Link
            to="/insights"
            className="mt-8 inline-flex items-center gap-2 bg-white text-[#0a2540] hover:bg-[#0a2540] hover:text-white font-600 px-7 py-3.5 rounded-full transition-colors"
          >
            {t("exploreThinking")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 flex gap-3">
          {slides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActive(i)}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === active ? "w-14 bg-white" : "w-8 bg-white/40 hover:bg-white/70"
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { t, localize } = useI18n();
  const [heroSlides, setHeroSlides] = useState(mockHeroSlides);
  const [rawInsights, setInsights] = useState(mockInsights);
  const [rawCapabilities, setCapabilities] = useState(mockCapabilities);
  const [rawStats, setStats] = useState(mockStats);
  const [magazineFeatures, setMagazineFeatures] = useState(mockMagazineFeatures);

  useEffect(() => {
    getHome()
      .then((d) => {
        if (d?.heroSlides?.length) setHeroSlides(d.heroSlides);
        if (d?.stats?.length) setStats(d.stats);
      })
      .catch(() => {});
    getInsights().then((d) => Array.isArray(d) && d.length && setInsights(d)).catch(() => {});
    getCapabilities().then((d) => Array.isArray(d) && d.length && setCapabilities(d)).catch(() => {});
    getMagazine().then((d) => d?.features?.length && setMagazineFeatures(d.features)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const insights = localize("insights", rawInsights);
  const capabilities = localize("capabilities", rawCapabilities);
  const stats = localize("stats", rawStats);

  return (
    <div>
      <Hero slides={heroSlides} />

      {/* Intro statement */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-display font-800 text-3xl md:text-5xl text-[#0a2540] leading-[1.1]">
              {t("homeIntroTitle")}
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-500 text-lg leading-relaxed">
              {t("homeIntroBody")}
            </p>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-2 text-[#0a6ed1] font-600 link-underline"
            >
              {t("aboutBpc")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured insights */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-sm font-700 uppercase tracking-widest text-[#0a6ed1]">
                {t("featuredInsights")}
              </span>
              <h2 className="font-display font-800 text-3xl md:text-4xl text-[#0a2540] mt-2">
                {t("latestThinking")}
              </h2>
            </div>
            <Link
              to="/insights"
              className="hidden md:inline-flex items-center gap-2 text-[#0a2540] font-600 hover:text-[#0a6ed1] transition-colors"
            >
              {t("viewAll")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
            <ArticleCard item={insights[0]} size="lg" />
            <ArticleCard item={insights[1]} size="lg" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {insights.slice(2, 5).map((i) => (
              <ArticleCard key={i.id} item={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="text-sm font-700 uppercase tracking-widest text-[#0a6ed1]">
              {t("whatWeDo")}
            </span>
            <h2 className="font-display font-800 text-3xl md:text-4xl text-[#0a2540] mt-2">
              {t("capsForGrowth")}
            </h2>
            <p className="mt-5 text-slate-500 text-lg leading-relaxed">
              {t("capsBody")}
            </p>
            <Link
              to="/capabilities"
              className="mt-6 inline-flex items-center gap-2 bg-[#0a2540] hover:bg-[#16355c] text-white font-600 px-6 py-3 rounded-full transition-colors"
            >
              {t("allCapabilities")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
            {capabilities.slice(0, 4).map((c) => (
              <Link
                key={c.id}
                to="/capabilities"
                className="group bg-white p-8 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <h3 className="font-display font-700 text-xl text-[#0a2540] group-hover:text-[#0a6ed1] transition-colors">
                    {c.title}
                  </h3>
                  <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-[#0a6ed1] transition-colors" />
                </div>
                <p className="mt-3 text-slate-500 text-[15px] leading-relaxed">
                  {c.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="bg-[#0a2540] text-white py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="font-display font-800 text-4xl md:text-6xl text-white">
                  {s.value}
                </div>
                <div className="mt-2 text-slate-300 text-[15px]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Magazine promo */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-sm font-700 uppercase tracking-widest text-[#0a6ed1]">
              {t("bpcMagazineLabel")}
            </span>
            <h2 className="font-display font-800 text-3xl md:text-4xl text-[#0a2540] mt-2">
              {t("ideasWorth")}
            </h2>
          </div>
          <Link
            to="/magazine"
            className="hidden md:inline-flex items-center gap-2 text-[#0a2540] font-600 hover:text-[#0a6ed1] transition-colors"
          >
            {t("readTheMagazine")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {magazineFeatures.map((m) => (
            <Link key={m.id} to="/magazine" className="group hover-zoom block">
              <div className="relative overflow-hidden rounded-lg aspect-[3/2] bg-slate-100">
                <img src={m.image} alt={m.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <span className="mt-4 inline-block text-xs font-700 uppercase tracking-wider text-[#0a6ed1]">
                {m.issue}
              </span>
              <h3 className="mt-2 font-display font-700 text-xl text-[#0a2540] group-hover:text-[#0a6ed1] transition-colors">
                {m.title}
              </h3>
              <p className="mt-2 text-slate-500 text-[15px]">{m.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Instagram feed */}
      <InstagramFeed />

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 pb-24">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a2540] to-[#16355c] px-8 md:px-16 py-16 md:py-20">
          <div className="max-w-2xl">
            <h2 className="font-display font-800 text-3xl md:text-5xl text-white leading-tight">
              {t("ctaTitle")}
            </h2>
            <p className="mt-5 text-blue-100 text-lg">
              {t("ctaBody")}
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-white text-[#0a2540] hover:bg-slate-100 font-600 px-7 py-3.5 rounded-full transition-colors"
            >
              {t("getInTouch")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
